import { useMemo } from "react";
import {
  useLocationsMetadata,
  useProductsMetadata,
  worldGroupDatum,
  allProductsDatum,
  LocationStringPrefixes,
} from "@atlas-int/data";
import { useCurrentVizType } from "../../routing/typedRoutes";
import { useTypedExploreParams } from "../paramSchemas";
import { AtlasNotFoundError } from "../../graphql/errors";

/**
 * Validates the `exporter`, `importer`, and `product` URL params for the
 * current explore viz against loaded metadata. Throws AtlasNotFoundError if
 * a param uses the expected prefix (`country-`, `group-`, `product-…-`) but
 * its ID isn't present in the metadata — the Atlas GraphQL API silently
 * returns empty data for unknown IDs rather than surfacing an error.
 *
 * Validation is skipped while metadata is loading to avoid false negatives.
 * Params that don't use a recognized prefix are left to the Zod schema's
 * default handling.
 */
export function useAssertValidExploreParams(): void {
  const vizType = useCurrentVizType();
  const [params] = useTypedExploreParams(vizType);
  const locations = useLocationsMetadata();
  const products = useProductsMetadata(params.productClass);

  const locationLookups = useMemo(() => {
    if (locations.status !== "success") return null;
    const countryPrefix = `${LocationStringPrefixes.Country}-`;
    const groupPrefix = `${LocationStringPrefixes.Group}-`;
    const stripPrefix = (value: string, prefix: string) =>
      value.startsWith(prefix) ? value.slice(prefix.length) : value;
    const countryIds = new Set(
      locations.data.countries.map((c) =>
        stripPrefix(String(c.countryId), countryPrefix),
      ),
    );
    const groupIds = new Set<string>([
      stripPrefix(worldGroupDatum.groupId, groupPrefix),
      ...locations.data.regions.map((r) =>
        stripPrefix(String(r.groupId), groupPrefix),
      ),
      ...locations.data.subregions.map((s) =>
        stripPrefix(String(s.groupId), groupPrefix),
      ),
    ]);
    return { countryIds, groupIds, countryPrefix, groupPrefix, stripPrefix };
  }, [locations]);

  const productLookups = useMemo(() => {
    if (products.status !== "success") return null;
    const stripProductPrefix = (value: string) =>
      value.replace(/^product-[A-Za-z0-9]+-/, "");
    const { section, twoDigit, fourDigit, sixDigit } = products.data;
    const ids = new Set<string>([
      stripProductPrefix(allProductsDatum.productId),
      ...(section ?? []).map((p) => stripProductPrefix(String(p.productId))),
      ...(twoDigit ?? []).map((p) => stripProductPrefix(String(p.productId))),
      ...(fourDigit ?? []).map((p) => stripProductPrefix(String(p.productId))),
      ...(sixDigit ?? []).map((p) => stripProductPrefix(String(p.productId))),
    ]);
    return { ids, stripProductPrefix };
  }, [products]);

  if (locationLookups) {
    const { countryIds, groupIds, countryPrefix, groupPrefix, stripPrefix } =
      locationLookups;
    for (const value of [params.exporter, params.importer]) {
      if (!value) continue;
      if (value.startsWith(countryPrefix)) {
        const bare = stripPrefix(value, countryPrefix);
        if (!countryIds.has(bare)) {
          throw new AtlasNotFoundError("country", value);
        }
      } else if (value.startsWith(groupPrefix)) {
        const bare = stripPrefix(value, groupPrefix);
        if (!groupIds.has(bare)) {
          throw new AtlasNotFoundError("country", value);
        }
      }
      // Unknown prefix → let the Zod schema's default kick in elsewhere.
    }
  }

  if (productLookups && params.product) {
    const bare = productLookups.stripProductPrefix(String(params.product));
    if (!productLookups.ids.has(bare)) {
      throw new AtlasNotFoundError("product", params.product);
    }
  }
}
